import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Card, CardContent, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import MainCarousel from "../MainCarousel/MainCarousel";
import AppPagination from "../Tools/AppPagination";
import { useDispatch, useSelector } from "react-redux";
import {
  getUsedBikesInWorkshop,
  getWorkshopDetails,
} from "../../State/Workshop/Action";

export default function WorkshopDetail() {
  const [activeTab, setActiveTab] = useState(0);
  const location = useLocation();
  const workshopId = new URLSearchParams(location.search).get("workshopId");
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const workshop = useSelector((store) => store.workshopResponse.workshop);
  const images = useSelector((store) => store.workshopResponse.workshop.images);
  const bikes = useSelector(
    (store) => store.workshopResponse.bikePaginated.usedBikes
  );
  const totalPages = useSelector(
    (store) => store.workshopResponse.bikePaginated.totalPages
  );

  useEffect(() => {
    dispatch(getWorkshopDetails(workshopId));
  }, [workshopId, dispatch]);

  useEffect(() => {
    dispatch(getUsedBikesInWorkshop(workshopId, page));
  }, [workshopId, page, dispatch]);

  const handleViewMore = (e, bike) => {
    navigate("/usedBikeDetail?bikeId=" + bike);
  };

  const tabs = [
    { id: 0, label: "Contact", icon: "📞" },
    { id: 1, label: "Address", icon: "📍" },
    { id: 2, label: "Bikes", icon: "🏍️" }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="p-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="card card-hover p-4">
                <CardContent>
                  <Typography variant="h6" className="font-bold text-gray-900 mb-2">
                    Workshop Name
                  </Typography>
                  <Typography variant="body1" className="text-gray-700">
                    {workshop.name}
                  </Typography>
                </CardContent>
              </Card>
              
              <Card className="card card-hover p-4">
                <CardContent>
                  <Typography variant="h6" className="font-bold text-gray-900 mb-2">
                    Owner Name
                  </Typography>
                  <Typography variant="body1" className="text-gray-700">
                    {workshop.ownerName}
                  </Typography>
                </CardContent>
              </Card>
              
              <Card className="card card-hover p-4">
                <CardContent>
                  <Typography variant="h6" className="font-bold text-gray-900 mb-2">
                    Primary Phone
                  </Typography>
                  <Typography variant="body1" className="text-gray-700">
                    {workshop.phoneNumber}
                  </Typography>
                </CardContent>
              </Card>
              
              <Card className="card card-hover p-4">
                <CardContent>
                  <Typography variant="h6" className="font-bold text-gray-900 mb-2">
                    Alternate Phone
                  </Typography>
                  <Typography variant="body1" className="text-gray-700">
                    {workshop.alternatePhoneNumber || "Not provided"}
                  </Typography>
                </CardContent>
              </Card>
              
              <Card className="card card-hover p-4">
                <CardContent>
                  <Typography variant="h6" className="font-bold text-gray-900 mb-2">
                    Email
                  </Typography>
                  <Typography variant="body1" className="text-gray-700">
                    {workshop.email}
                  </Typography>
                </CardContent>
              </Card>
              
              <Card className="card card-hover p-4">
                <CardContent>
                  <Typography variant="h6" className="font-bold text-gray-900 mb-2">
                    Experience
                  </Typography>
                  <Typography variant="body1" className="text-gray-700">
                    {workshop.yearsOld} year(s)
                  </Typography>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        );
      
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="p-6"
          >
            <div className="grid gap-6">
              <Card className="card card-hover p-6">
                <CardContent>
                  <Typography variant="h5" className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <LocationOnIcon className="text-primary-600" />
                    Workshop Address
                  </Typography>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Typography variant="body2" className="font-semibold text-gray-600 min-w-[100px]">
                        Shop #:
                      </Typography>
                      <Typography variant="body1" className="text-gray-900">
                        {workshop.shopNumber}
                      </Typography>
                    </div>
                    <div className="flex items-start gap-3">
                      <Typography variant="body2" className="font-semibold text-gray-600 min-w-[100px]">
                        Address:
                      </Typography>
                      <Typography variant="body1" className="text-gray-900">
                        {workshop.address}
                      </Typography>
                    </div>
                    <div className="flex items-start gap-3">
                      <Typography variant="body2" className="font-semibold text-gray-600 min-w-[100px]">
                        City:
                      </Typography>
                      <Typography variant="body1" className="text-gray-900">
                        {workshop.city}
                      </Typography>
                    </div>
                    <div className="flex items-start gap-3">
                      <Typography variant="body2" className="font-semibold text-gray-600 min-w-[100px]">
                        State:
                      </Typography>
                      <Typography variant="body1" className="text-gray-900">
                        {workshop.state}
                      </Typography>
                    </div>
                    <div className="flex items-start gap-3">
                      <Typography variant="body2" className="font-semibold text-gray-600 min-w-[100px]">
                        Zip Code:
                      </Typography>
                      <Typography variant="body1" className="text-gray-900">
                        {workshop.zipCode}
                      </Typography>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        );
      
      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="p-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bikes?.map((bike) => (
                <motion.div
                  key={bike.bikeId}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="card card-hover overflow-hidden">
                    <div className="relative">
                      <img
                        onClick={(e) => handleViewMore(e, bike.bikeId)}
                        className="w-full h-52 object-cover cursor-pointer"
                        src={bike.imageUrl}
                        alt="bike_image"
                      />
                      <div className="absolute top-3 right-3 bg-white/90 rounded-full p-2">
                        <LocationOnIcon className="text-primary-600" fontSize="small" />
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <Typography variant="h6" className="font-bold text-gray-900 mb-2">
                        {bike.brand} {bike.model}
                      </Typography>
                      <Typography variant="body2" className="text-gray-500 mb-2">
                        Year: {bike.purchaseYear}
                      </Typography>
                      <Typography variant="h5" className="font-bold text-primary-600 mb-3">
                        ₹{bike.price}/-
                      </Typography>
                      <div className="flex items-center text-gray-600 mb-4">
                        <LocationOnIcon fontSize="small" />
                        <Typography variant="body2" className="ml-1">
                          {bike.sellerCity}
                        </Typography>
                      </div>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={(e) => handleViewMore(e, bike.bikeId)}
                        className="btn-primary"
                      >
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            {bikes?.length === 0 && (
              <div className="text-center py-12">
                <Typography variant="h6" className="text-gray-500 mb-2">
                  No bikes available at this workshop
                </Typography>
                <Typography variant="body2" className="text-gray-400">
                  Check back later for new listings
                </Typography>
              </div>
            )}
            <div className="flex justify-center mt-8">
              <AppPagination setPage={setPage} totalPages={totalPages} />
            </div>
          </motion.div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen gradient-bg pt-20 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {workshop && (
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 text-white shadow-strong">
              <Typography variant="h3" className="font-bold mb-2">
                {workshop.name}
              </Typography>
              <Typography variant="body1" className="text-white/80">
                Professional Workshop Services
              </Typography>
            </div>
          </motion.div>
        )}
        
        <div className="mb-8">
          {images && <MainCarousel imageList={images} />}
        </div>
        
        {workshop && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Custom Tabs */}
            <div className="bg-white rounded-2xl shadow-strong overflow-hidden">
              {/* Tab Navigation */}
              <div className="flex border-b border-gray-200">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 px-6 py-4 font-semibold text-sm transition-all duration-300 ${
                      activeTab === tab.id
                        ? "bg-primary-600 text-white border-b-2 border-primary-600"
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </div>
              
              {/* Tab Content */}
              <div className="min-h-[400px]">
                {renderTabContent()}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
